/** @jsxImportSource @emotion/react */
// UT Matrix 정합화 Comparison Report List 페이지
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@mui/material';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import Datepicker from 'react-tailwindcss-datepicker';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import UserModal from 'components/modals/common/UserModal';
import CustomDialog from 'components/modals/common/CustomDialog';
import { Employee } from 'models/admin/Employee';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { SearchParam, UtMatrixMasterList } from 'models/ut/UtMatrixList';
import {
  ComparisonReportSearchParam,
  UtMatrixComparisonReportList,
} from 'models/ut/UtMatrixComparisonReportList';
import { getUtMatrixMasterList } from 'apis/ut/UtMatrixList';
import {
  deleteReportList,
  getUtMatrixComparisonReportList,
} from 'apis/ut/UtMatrixComparisonReportList';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { useLocation, useNavigate } from 'react-router-dom';
import { Code } from 'models/common/CommonCode';
import moment from 'moment';
import useSessionStore from 'stores/useSessionStore';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { ChatbotButton } from 'components/buttons/ChatbotButton';
import { useChatbotStore } from 'stores/useChatbotStore';
import { CrudCode } from 'models/common/Edit';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useCommonModal } from 'hooks/useCommonModal';
import { useLoading } from 'components/process/Loading';
import { hasRole } from 'utils/SessionUtil';

/**
 * @desc UT Matrix 정합화 list
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.25   jihwan89       최초 페이지 생성
 * 2026.10.19   jihwan89       CSR E20260921-000266 UT Matrix 정합화 Report – 삭제 기능 추가
 */

const UtMatrixComparisonReportListPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { openLoading } = useLoading();
  const navigate = useNavigate();
  const userSession = useSessionStore();

  const location = useLocation();

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [hitTest, setHitTest] = useState<any>();
  const [chatOpen, setChatOpen] = useState(false);

  // 그리드 데이터
  const [reportRowData, setReportRowData] = useState<UtMatrixComparisonReportList[]>([]);

  // 콤보박스 옵션 (UT Matrix 명)
  const [utmOptions, setUtmOptions] = useState<Code[]>([]);
  // AI Report 팝업 (화면 미정 — 껍데기만)
  const [aiReportRptId, setAiReportRptId] = useState<string | null>(null);

  // 조회조건 상태
  const today = moment().format('YYYY-MM-DD');
  const bfOneYear = moment().subtract(1, 'years');
  const startYears = bfOneYear.startOf('month').format('YYYY-MM-DD');

  const initSearchParam: ComparisonReportSearchParam = {
    rptNm: '',
    utmIds: '',
    startDate: startYears,
    endDate: today,
    wrtUserId: '',
    wrtUserNm: '',
  };
  const [searchParam, setSearchParam] = useState<ComparisonReportSearchParam>(initSearchParam);

  // 작성자 검색 팝업
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [wrtUserNm, setWrtUserNm] = useState<string>('');

  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const openChatbot = useChatbotStore((s) => s.openPanel);

  useEffect(() => {
    initCondition();
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
  }, []);

  /**
   * 목록 조회 — 최초 진입 + 다른 화면에서 이 화면으로 다시 들어올 때마다.
   *
   * MDI 탭 셸에서는 이미 열린 탭으로 이동하면 컴포넌트가 재마운트되지 않아
   * mount 전용 useEffect만으로는 방금 만든 리포트가 목록에 나타나지 않는다.
   * (UT Matrix Summary 화면에서 리포트 생성 후 이 화면으로 이동하는 경로)
   * location.key는 같은 경로로 다시 navigate해도 바뀌므로 재조회 트리거로 쓴다.
   */
  useEffect(() => {
    btnSearch();
  }, [location.key]);

  // UT Matrix 명 콤보박스 데이터 로드
  const initCondition = async () => {
    const masterList = await getUtMatrixMasterList({
      curYn: 'Y',
    } as SearchParam);
    if (masterList) {
      const options: Code[] = masterList
        .map((item: UtMatrixMasterList) => ({
          cmnCd: item.utmId || '',
          cmnCdNm: `${item.utmNm || ''}(${item.verNo || ''})`,
        }))
        .sort((a, b) => (a.cmnCdNm || '').localeCompare(b.cmnCdNm || ''));
      setUtmOptions(options);
    }
  };

  /**
   * 목록 1행의 기준/비교대상/참조 표시값을 채운다.
   * BE가 mstDisp/tgtDisp/refDisp를 주면 그대로 쓰고, 아직 utmNm(콤마 구분 문자열)만
   * 오는 동안에는 SORT_ORD 순서(1=기준, 2=비교대상, 3=참조)로 잘라서 채운다.
   */
  const fillTargetDisplay = (row: UtMatrixComparisonReportList) => {
    if (row.mstDisp || row.tgtDisp || row.refDisp) return row;
    const parts = (row.utmNm || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      ...row,
      mstDisp: parts[0] ?? '',
      tgtDisp: parts[1] ?? '',
      refDisp: parts[2] ?? '',
    };
  };

  // 조회
  const btnSearch = () => {
    getUtMatrixComparisonReportList(searchParam).then((result: UtMatrixComparisonReportList[]) => {
      if (result !== null) {
        setReportRowData((result || []).map(fillTargetDisplay));
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  // 초기화
  const btnReload = () => {
    setWrtUserNm('');
    setSearchParam(initSearchParam);
  };

  /**
   * 상세분석 — 분석 Report/전기/기계 탭으로 구성된 리포트 상세 화면으로 이동.
   * rptNm은 분석 Report 탭의 보고서 발행 파일명에 쓰인다.
   */
  const fnMoveReportDetail = (rptId: string, rptNm?: string) => {
    navigate('/ut/comparison-report/detail', {
      state: {
        upprMnuUrl: '/ut/comparison-report/list',
        rptId,
        rptNm,
        mnuNm: t('ut.label.UT Matrix 정합화 report', 'UT Matrix 정합화 report'),
      },
    });
  };

  // 그리드 레이아웃 정의
  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'rptId',
        visible: false,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        cellTemplate: (grid: any) => grid.row._idx + 1,
        isReadOnly: true,
      },
      {
        binding: 'rptNm',
        header: String(t('ut.label.분석명', '분석명')),
        width: '*',
        minWidth: 200,
        isReadOnly: true,
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '${item.rptNm}',
          click: (e: any, ctx: any) => {
            fnMoveReportDetail(ctx.item?.rptId, ctx.item?.rptNm);
          },
        }),
        // cellTemplate: CellMaker.makeLink({
        //   text: '${item.rptNm}',
        //   click: (e: any, ctx: any) => {
        //     fnSearchRptNm(ctx.item);
        //   },
        // }),
      },
      /*
        대상 UT Matrix는 기준/비교대상/참조 3단으로 보여준다
        (ref/html/ut_matrix_rpt_demo.html 1050~1051줄).
        BE가 mstDisp/tgtDisp/refDisp를 내려주면 그대로 쓰고, 아직 utmNm(콤마 구분)만
        오는 동안에는 SORT_ORD 순서(기준,비교대상,참조)로 잘라 채운다.
      */
      {
        /*
          ⚠ 자식 컬럼에 cssClass:'noMerge'를 주면 안 된다.
          CustomGrid는 인접 컬럼 중 하나라도 noMerge면 헤더 병합을 끊어서(CustomGrid.tsx:482)
          "UT Matrix" 부모 헤더가 3조각으로 쪼개진다.
          (UtMatrixSummaryPage는 그룹당 자식이 1개뿐이라 noMerge가 필요했던 경우다)
        */
        header: String(t('ut.label.UT Matrix', 'UT Matrix')),
        columns: [
          {
            binding: 'mstDisp',
            header: String(t('ut.label.기준', '기준')),
            width: '*',
            minWidth: 150,
            isReadOnly: true,
            align: 'left',
          },
          {
            binding: 'tgtDisp',
            header: String(t('ut.label.비교대상', '비교대상')),
            width: '*',
            minWidth: 150,
            isReadOnly: true,
            align: 'left',
          },
          {
            binding: 'refDisp',
            header: String(t('ut.label.참조', '참조')),
            width: '*',
            minWidth: 150,
            isReadOnly: true,
            align: 'left',
          },
        ],
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('ut.label.작성자', '작성자')),
        width: 120,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('ut.label.작성일', '작성일')),
        width: 150,
        isReadOnly: true,
        align: 'center',
      },
    ],
    []
  );

  // 그리드 초기화
  const onInitialized = (grid: any) => {
    setFlexRef(grid);
    gridRef.current = grid;

    // 상세분석 / Report 버튼 클릭 처리
    grid.hostElement.addEventListener('click', (e: any) => {
      if (grid.rows.length === 0) return;
      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType !== 1) return; // 셀이 아니면 무시

      if (!(e.target instanceof HTMLButtonElement)) return;
      const item = grid.rows[ht.row].dataItem;
      if (!item?.rptId) return;

      switch (e.target.id) {
        case 'btnRptDetail':
          fnMoveReportDetail(item.rptId, item.rptNm);
          break;
        case 'btnRptAi':
          setAiReportRptId(item.rptId);
          break;
      }
    });
  };

  // 엑셀 다운로드
  const btnExcelExport = () => {
    if (flexRef) {
      const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
        includeColumnHeaders: true,
        includeRowHeaders: true,
      });
      book.sheets[0].name = 'UT Matrix 정합화 Report List';
      book.saveAsync(
        getExcelFileName(t('ut.label.UT Matrix 정합화 Report List', 'UT Matrix 정합화 Report List'))
      );
    }
  };

  // * 2026.10.19 jihwan89 CSR E20260921-000266 UT Matrix 정합화 Report – 삭제 기능 추가
  const btnDelete = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (!selectedRows || selectedRows.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return;
    }

    const selectedData = selectedRows.map(({ dataItem }) => dataItem);

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.삭제 하시겠습니까?', '삭제 하시겠습니까?'),
      yesCallback: async () => {
        openLoading(true);
        const responseMaster = await deleteReportList(selectedData).finally(() =>
          openLoading(false)
        );

        if (responseMaster == null || responseMaster?.successOrNot === 'N') {
          openMessageBar({
            type: 'error',
            content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          return;
        }
        openMessageBar({
          type: 'confirm',
          content: t('com.label.삭제 되었습니다.', '삭제 되었습니다.'),
        });

        // handleSearchRequestViewRead({ utmId: responseMaster.data });
        // 임시저장 제외한 동작 후 탭닫고 목록 열기
        btnSearch();
      },
    });
  };

  return (
    <ContainerLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('ut.label.분석명', '분석명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="rptNm"
                  placeholder={String(
                    t('ut.label.분석명을 입력해 주세요.', '분석명을 입력해 주세요.')
                  )}
                  value={searchParam?.rptNm}
                  onChange={(e) => setSearchParam({ ...searchParam, rptNm: e.target.value })}
                  onKeyDown={(e) => (e.key === 'Enter' ? btnSearch() : '')}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.UT Matrix 명', 'UT Matrix 명')}</label>
                <MultiComboBox
                  id="utmId"
                  options={utmOptions}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.utmIds ? searchParam.utmIds.split(',') : []}
                  onChange={(value: string[]) => {
                    setSearchParam({ ...searchParam, utmIds: value.join(',') });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.작성일', '작성일')}</label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    value={{
                      startDate: searchParam?.startDate || null,
                      endDate: searchParam?.endDate || null,
                    }}
                    onChange={(value: any) => {
                      setSearchParam({
                        ...searchParam,
                        startDate: String(value?.startDate || ''),
                        endDate: String(value?.endDate || ''),
                      });
                    }}
                    i18n={userSession.langCd}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                  />
                </DatePickerWrap>
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.작성자', '작성자')}</label>
                <CustomInputWithSearch
                  name="wrtUserId"
                  className="find"
                  value={wrtUserNm || ''}
                  defaultValue={wrtUserNm || ''}
                  placeholder={String(
                    t('ut.label.작성자를 선택해 주세요.', '작성자를 선택해 주세요.')
                  )}
                  onChange={(e) => {
                    setWrtUserNm(e.target.value);
                    setSearchParam({
                      ...searchParam,
                      wrtUserId: '',
                      wrtUserNm: e.target.value,
                    });
                  }}
                  onSearchClick={() => {
                    setOpenUserModal(true);
                  }}
                  onKeyDown={(e) => (e.key === 'Enter' ? setOpenUserModal(true) : '')}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" onClick={btnReload} disableRipple />
            <Button css={IconButton.button} className="find" onClick={btnSearch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix 정합화 Report List', 'UT Matrix 정합화 Report List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(reportRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {/*
          리포트 생성 진입점은 "UT Matrix Summary" 화면의 [정합화 Report] 버튼 하나로
          통일했다. 이 화면은 조회 전용이라 대상 선택/신규 분석 버튼을 두지 않는다.
        */}
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={reportRowData}
        allowPinning={false}
        height={500}
        initialized={onInitialized}
        showCheckAll={false}
        isSelector={true}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="delete" disableRipple onClick={btnDelete}>
            {t('com.button.삭제', '삭제')}
          </Button>
        </GlobalBtnGroup>
      )}
      {/*
        Report 버튼 — AI 분석결과 팝업. 화면 미정이라 자리만 잡아둔다.
        컴포넌트가 나오면 아래 자리에 <UtMatrixAiReportPopup rptId={aiReportRptId} ... /> 로 교체.
      */}
      {aiReportRptId && (
        <CustomDialog
          title={t('ut.label.AI 분석 Report', 'AI 분석 Report')}
          size="lg"
          open={!!aiReportRptId}
          onClose={() => setAiReportRptId(null)}
          onCancel={() => setAiReportRptId(null)}
        >
          <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 12 }}>
            {t('ut.label.준비 중입니다.', '준비 중입니다.')} ({aiReportRptId})
          </div>
        </CustomDialog>
      )}
      {isOpenUserModal && (
        <UserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.작성자 조회', '작성자 조회')}
          singleSelect={true}
          onCallback={(result: any) => {
            const users = result as Employee;
            setSearchParam({ ...searchParam, wrtUserId: users.userId });
            setWrtUserNm(users.empNm || '');
          }}
          condition={{ empNm: wrtUserNm }}
        />
      )}
      <ChatbotButton onClick={() => openChatbot('ut-matrix')} />
    </ContainerLayout>
  );
};

export default UtMatrixComparisonReportListPage;
